import React from "react";
import { AuditLogDiff } from "@common/types/auditLog";
import {
    AccrualLineDelta,
    AccrualLineField,
    AccrualLineFieldValue,
    AccrualLineSnapshot,
} from "@common/helpers/accrualLineDeltas";
import { getAccrualLineFieldLabel } from "./lineFieldLabels";
import {
    AccrualLabelResolver,
    DisplayedValue,
    describeDiffSide,
    describeLineFieldValue,
    getAccrualFieldLabel,
    getLineFieldSummaries,
} from "./accrualValueFormat";

type ValueChangeKind =
    | "set"
    | "cleared"
    | "changed"
    | "indistinguishable"
    | "unchanged";

/**
 * `unchanged` means nothing was ever visible on either side (`"" -> null`), so
 * the change can be dropped. `indistinguishable` means the two sides render to
 * the same text even though the raw values differ (a same-day timestamp shift,
 * for example) — those must not claim a "from X to X" change, but they still
 * need a mention so the change is not silently lost.
 */
function getValueChangeKind(
    before: DisplayedValue,
    after: DisplayedValue,
): ValueChangeKind {
    if (before.isEmpty && after.isEmpty) {
        return "unchanged";
    }
    if (before.text === after.text) {
        return "indistinguishable";
    }
    if (before.isEmpty) {
        return "set";
    }
    if (after.isEmpty) {
        return "cleared";
    }
    return "changed";
}

export function joinPhrases(phrases: React.ReactNode[]): React.ReactNode {
    if (phrases.length === 0) {
        return null;
    }
    if (phrases.length === 1) {
        return phrases[0];
    }

    return phrases.map((phrase, index) => {
        if (index === 0) {
            return <React.Fragment key={index}>{phrase}</React.Fragment>;
        }
        if (index === phrases.length - 1) {
            return <React.Fragment key={index}> and {phrase}</React.Fragment>;
        }
        return <React.Fragment key={index}>, {phrase}</React.Fragment>;
    });
}

/**
 * Top-level field change, rendered as a full predicate that follows the author
 * ("Bob set the total to 100"). Returns null only when neither side ever had a
 * visible value.
 */
export function renderFieldPhrase({
    diff,
    currency,
}: {
    diff: AuditLogDiff;
    currency: string | null;
}): React.ReactNode | null {
    const label = getAccrualFieldLabel(diff.field);
    const before = describeDiffSide({
        refValue: diff.before,
        field: diff.field,
        currency,
    });
    const after = describeDiffSide({
        refValue: diff.after,
        field: diff.field,
        currency,
    });

    switch (getValueChangeKind(before, after)) {
        case "unchanged":
            return null;
        case "indistinguishable":
            return <>updated the {label}</>;
        case "set":
            return (
                <>
                    set the {label} to <strong>{after.text}</strong>
                </>
            );
        case "cleared":
            return (
                <>
                    cleared the {label} (was <strong>{before.text}</strong>)
                </>
            );
        default:
            return (
                <>
                    changed the {label} from <strong>{before.text}</strong> to{" "}
                    <strong>{after.text}</strong>
                </>
            );
    }
}

/**
 * Line field change, rendered as a verbless clause so it composes after the
 * "updated line item X:" prefix. Returns null only when neither side ever had a
 * visible value.
 */
function renderLineFieldPhrase({
    field,
    before,
    after,
    currency,
    resolver,
}: {
    field: AccrualLineField;
    before: AccrualLineFieldValue;
    after: AccrualLineFieldValue;
    currency: string | null;
    resolver: AccrualLabelResolver;
}): React.ReactNode | null {
    const label = getAccrualLineFieldLabel(field);
    const beforeValue = describeLineFieldValue({
        field,
        value: before,
        currency,
        resolver,
    });
    const afterValue = describeLineFieldValue({
        field,
        value: after,
        currency,
        resolver,
    });

    switch (getValueChangeKind(beforeValue, afterValue)) {
        case "unchanged":
            return null;
        case "indistinguishable":
            return <>{label} updated</>;
        case "set":
            return (
                <>
                    {label} set to <strong>{afterValue.text}</strong>
                </>
            );
        case "cleared":
            return (
                <>
                    {label} cleared (was <strong>{beforeValue.text}</strong>)
                </>
            );
        default:
            return (
                <>
                    {label} from <strong>{beforeValue.text}</strong> to{" "}
                    <strong>{afterValue.text}</strong>
                </>
            );
    }
}

function renderLineFieldSummary(
    line: AccrualLineSnapshot,
    resolver: AccrualLabelResolver,
): React.ReactNode {
    const summaries = getLineFieldSummaries(line, resolver);
    if (summaries.length === 0) {
        return null;
    }

    return (
        <>
            {" "}
            with{" "}
            {joinPhrases(
                summaries.map(({ field, label, text }) => (
                    <React.Fragment key={field}>
                        {label} <strong>{text}</strong>
                    </React.Fragment>
                )),
            )}
        </>
    );
}

export function renderLineDeltaPhrase({
    delta,
    label,
    currency,
    resolver,
}: {
    delta: AccrualLineDelta;
    label: string;
    currency: string | null;
    resolver: AccrualLabelResolver;
}): React.ReactNode | null {
    if (delta.type === "added") {
        return (
            <>
                added line item {label}
                {renderLineFieldSummary(delta.line, resolver)}
            </>
        );
    }

    if (delta.type === "removed") {
        return (
            <>
                removed line item {label}
                {renderLineFieldSummary(delta.line, resolver)}
            </>
        );
    }

    const lineCurrency = delta.line.currency ?? currency;
    const changePhrases = (delta.fieldChanges ?? [])
        .map((change) =>
            renderLineFieldPhrase({
                field: change.field,
                before: change.before,
                after: change.after,
                currency: lineCurrency,
                resolver,
            }),
        )
        .filter((phrase) => phrase !== null);

    if (changePhrases.length === 0) {
        return null;
    }

    return (
        <>
            updated line item {label}: {joinPhrases(changePhrases)}
        </>
    );
}
